<template>
  <el-dialog
    title="批量编辑"
    :visible.sync="visible"
    width="600px"
    :before-close="handleClose"
    append-to-body
    custom-class="batch-edit-dialog"
  >
    <el-form ref="form" :model="form" label-width="120px">
      <!-- 微信公众号用户部分 -->
      <el-form-item label="微信公众号用户:">
        <div class="upload-section">
          <el-upload
            ref="upload"
            :auto-upload="false"
            :on-change="handleFileChange"
            :show-file-list="false"
            accept=".xlsx,.xls"
          >
            <el-button size="small">选择导入</el-button>
          </el-upload>
        </div>
        <div v-if="form.fileName" class="file-info">
          <div class="file-name">
            <el-link 
              type="primary" 
              @click="handleDownloadFile"
              :underline="false"
            >
              {{ form.fileName }}
            </el-link>
          </div>
          <div v-if="form.userCount" class="user-count">用户数: {{ form.userCount }}</div>
        </div>
      </el-form-item>

      <!-- 标签操作部分 -->
      <el-form-item label="标签操作:">
        <el-radio-group v-model="form.operationType">
          <el-radio label="add">新增</el-radio>
          <el-radio label="delete">删除</el-radio>
        </el-radio-group>
        
        <div style="margin-top: 16px;">
          <el-button 
            size="small" 
            @click="handleSelectTags"
          >
            选择标签
          </el-button>
        </div>
        
        <!-- 已选择的标签显示 -->
        <div v-if="form.submitItems.length > 0" class="selected-tags">
          <el-tag
            v-for="tag in form.submitItems"
            :key="tag.wechatTagId"
            style="margin-right: 8px; margin-top: 8px;"
          >
            {{ tag.wechatTagName }}
          </el-tag>
        </div>
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="loading">提交并同步</el-button>
    </span>

    <!-- 标签选择弹窗 -->
    <TagSelectionDialog
      :visible.sync="tagSelectionVisible"
      :wechatTags="form.selectedTagIds"
      @submit="handleTagSelectionSubmit"
    />

    <!-- 同步结果弹窗 -->
    <SyncResultDialog
      :visible.sync="syncResultVisible"
      :isDeleteMode="form.operationType === 'delete'"
      ref="syncResultDialog"
      @cancel="handleSyncCancel"
    />
  </el-dialog>
</template>

<script>
import TagSelectionDialog from './TagSelectionDialog.vue'
import SyncResultDialog from './SyncResultDialog.vue'
import { batchUpdateWeChatUserTags } from '@/api/operationsManage/weChatUserManage'
import * as XLSX from 'xlsx'

export default {
  name: 'BatchEditDialog',
  components: {
    TagSelectionDialog,
    SyncResultDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      loading: false,
      tagSelectionVisible: false,
      syncResultVisible: false,
      form: {
        fileName: '',
        userCount: null,
        file: null,
        operationType: 'delete', // 'add' or 'delete'
        selectedTagIds: [],
        submitItems: []
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        fileName: '',
        userCount: null,
        file: null,
        operationType: 'delete',
        selectedTagIds: [],
        submitItems: []
      }
    },
    async handleFileChange(file) {
      this.form.file = file.raw
      this.form.fileName = file.name
      
      // 解析 Excel 文件获取用户数
      try {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            
            // 获取第一个工作表
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]
            
            // 将工作表转换为 JSON 数组
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
            
            // 计算有效行数（排除空行和表头）
            // 假设第一行是表头，从第二行开始计算
            const validRows = jsonData.filter((row, index) => {
              // 排除表头和空行
              return index > 0 && row.some(cell => cell !== null && cell !== undefined && cell !== '')
            })
            
            this.form.userCount = validRows.length
          } catch (error) {
            console.error('解析 Excel 文件失败:', error)
            this.$message.warning('无法解析文件，用户数显示可能不准确')
            this.form.userCount = null
          }
        }
        reader.readAsArrayBuffer(file.raw)
      } catch (error) {
        console.error('读取文件失败:', error)
        this.form.userCount = null
      }
    },
    handleDownloadFile() {
      if (!this.form.file) return
      
      // 创建下载链接
      const url = URL.createObjectURL(this.form.file)
      const link = document.createElement('a')
      link.href = url
      link.download = this.form.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      // 释放 URL 对象
      URL.revokeObjectURL(url)
    },
    handleSelectTags() {
      this.tagSelectionVisible = true
    },
    handleTagSelectionSubmit({ wechatTags, submitItems }) {
      this.form.selectedTagIds = wechatTags
      this.form.submitItems = submitItems
    },
    handleClose() {
      this.$emit('close')
    },
    handleSyncCancel() {
      this.resetForm()
      this.handleClose()
    },
    async handleSubmit() {
      // 验证必填项
      if (!this.form.file) {
        this.$message.warning('请选择导入文件')
        return
      }
      if (this.form.selectedTagIds.length === 0) {
        this.$message.warning('请选择标签')
        return
      }

      // 显示同步结果弹窗并开始同步
      this.syncResultVisible = true
      this.$nextTick(() => {
        this.$refs.syncResultDialog.startSync()
      })

      this.loading = true
      try {
        // 准备提交数据
        const formData = new FormData()
        const queryParams = {}
        
        // 根据操作类型设置不同的参数
        if (this.form.operationType === 'add') {
          // 新增：使用 tagOpenidFile (formData) 和 tagVxTagIds (query)
          formData.append('tagOpenidFile', this.form.file)
          // tagVxTagIds 作为 query 参数传递（数组格式）
          if (this.form.selectedTagIds && this.form.selectedTagIds.length > 0) {
            queryParams.tagVxTagIds = this.form.selectedTagIds.join(',')
          }
        } else {
          // 删除：使用 untagOpenidFile (formData) 和 untagVxTagIds (query)
          formData.append('untagOpenidFile', this.form.file)
          // untagVxTagIds 作为 query 参数传递（数组格式）
          if (this.form.selectedTagIds && this.form.selectedTagIds.length > 0) {
            queryParams.untagVxTagIds = this.form.selectedTagIds.join(',')
          }
        }

        this.$emit('submit', {
          file: this.form.file,
          operationType: this.form.operationType,
          wechatTagIds: this.form.selectedTagIds
        })

        const res = await batchUpdateWeChatUserTags(formData, queryParams)
        
        // 设置同步结果
        const resultData = this.form.operationType === 'add' ? res.data.data.taggingResult : res.data.data.untaggingResult
        this.$refs.syncResultDialog.setResult({
          status: resultData.status || 'fail',
          successCount: resultData.successCount || 0,
          failCount: resultData.failCount || 0,
          failUserArray: resultData.failMsg || []
        })
      } catch (error) {
        console.error('提交失败:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-section {
  display: flex;
  align-items: center;
}

.file-info {
  
  .file-name {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #303133;
  }
  
  .user-count {
    font-size: 14px;
    color: #606266;
  }
}

.selected-tags {
  margin-top: 12px;
  min-height: 40px;
}
</style>
